/**
 * ATS Report V3 Types
 *
 * Two-tier progressive analysis:
 *   - Quick (tier: 'quick'): ~3-5s, gpt-4o-mini — score + summary + critical issues
 *   - Full  (tier: 'full'):  ~10-20s, gpt-4o   — sections, bullet rewrites, error detection
 *
 * The UI renders whatever tier is available, upgrading in-place when full arrives.
 */

// ---------------------------------------------------------------------------
// Enums & primitives
// ---------------------------------------------------------------------------

/** How serious a finding is */
export type FindingSeverity = 'critical' | 'warning' | 'info';

/** Type of detected error in CV content */
export type DetectedErrorType =
  | 'typo'
  | 'grammar'
  | 'formatting'
  | 'encoding'
  | 'inconsistency';

/** Improvement priority */
export type ImprovementPriority = 'high' | 'medium' | 'low';

/** Display modes for the ATSReport component */
export type ATSReportDisplayMode = 'compact' | 'full' | 'card';

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

/** A single finding within a section */
export interface Finding {
  severity: FindingSeverity;
  message: string;
  section?: string;
}

/** Before / After rewrite suggestion for a single bullet */
export interface BulletRewrite {
  original: string;
  rewritten: string;
  reason: string;
}

/** A detected error in the CV text (typos, grammar, encoding issues) */
export interface DetectedError {
  type: DetectedErrorType;
  text: string;
  suggestion: string;
  location?: string;
}

/** An actionable improvement recommendation */
export interface ATSImprovement {
  priority: ImprovementPriority;
  category: string;
  action: string;
  reason: string;
}

// ---------------------------------------------------------------------------
// Section scores (used in full tier)
// ---------------------------------------------------------------------------

/** Base section score — every section has these */
export interface ATSSectionScoreBase {
  score: number; // 1-10
  label: string;
  feedback: string;
  tips: string[];
  findings: Finding[];
}

/** Contact information section */
export interface ContactSectionScore extends ATSSectionScoreBase {
  hasEmail: boolean;
  hasPhone: boolean;
  hasLinkedIn: boolean;
  hasLocation: boolean;
}

/** Professional summary section */
export interface SummarySectionScore extends ATSSectionScoreBase {
  hasSummary: boolean;
  wordCount?: number;
  rewrites?: BulletRewrite[];
}

/** A single role within the experience section */
export interface ExperienceRoleScore {
  company: string;
  title: string;
  score: number; // 1-10
  feedback: string;
  bulletFeedback?: BulletRewrite[];
}

/** Experience section with per-role breakdown */
export interface ExperienceSectionScore extends ATSSectionScoreBase {
  roles: ExperienceRoleScore[];
}

/** Skills section with role-aware analysis */
export interface SkillsSectionScore extends ATSSectionScoreBase {
  detectedRole?: string;
  relevantSkills: string[];
  missingSkills: string[];
  redundantSkills?: string[];
}

/** Education section */
export interface EducationSectionScore extends ATSSectionScoreBase {}

// ---------------------------------------------------------------------------
// Category scores (always present in both tiers)
// ---------------------------------------------------------------------------

/** One of the three top-level scoring categories */
export interface ATSReportCategory {
  score: number; // 1-10
  label: string;
  weight: number; // percentage (30 / 40 / 30)
}

export interface ATSReportCategories {
  completeness: ATSReportCategory;
  roleAlignment: ATSReportCategory;
  impact: ATSReportCategory;
}

// ---------------------------------------------------------------------------
// Report tiers (discriminated union on `tier`)
// ---------------------------------------------------------------------------

/** Fields common to both tiers */
interface ATSReportV3Base {
  version: 3;
  overallScore: number; // 0-100
  summary: string;
  categories: ATSReportCategories;
  criticalIssues: string[];
  detectedRole?: string;
}

/** Quick tier — returned in ~3-5 seconds */
export interface ATSReportV3Quick extends ATSReportV3Base {
  tier: 'quick';
}

/** Full tier — returned in ~10-20 seconds */
export interface ATSReportV3Full extends ATSReportV3Base {
  tier: 'full';
  sections: {
    contact: ContactSectionScore;
    summary: SummarySectionScore;
    experience: ExperienceSectionScore;
    skills: SkillsSectionScore;
    education: EducationSectionScore;
  };
  errors: DetectedError[];
  improvements: ATSImprovement[];
}

/** Discriminated union — the UI checks `tier` to decide what to render */
export type ATSReportV3 = ATSReportV3Quick | ATSReportV3Full;

// ---------------------------------------------------------------------------
// Wrapper (mirrors FullATSScore pattern — format + content)
// ---------------------------------------------------------------------------

/**
 * Complete ATS score: format analysis + V3 report.
 * Stored in SavedCV alongside legacy `atsScore` during migration.
 */
export interface FullATSScoreV3 {
  formatScore: number;
  formatIssues: string[];
  report: ATSReportV3;
}

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

/** Check if a report is the full tier */
export function isFullReport(report: ATSReportV3): report is ATSReportV3Full {
  return report.tier === 'full';
}

/** Check if a report is the quick tier */
export function isQuickReport(report: ATSReportV3): report is ATSReportV3Quick {
  return report.tier === 'quick';
}

/** Check if an ATS score is V3 */
export function isV3Score(
  score: unknown,
): score is FullATSScoreV3 {
  return (
    typeof score === 'object' &&
    score !== null &&
    'report' in score &&
    typeof (score as FullATSScoreV3).report === 'object' &&
    (score as FullATSScoreV3).report !== null &&
    'version' in (score as FullATSScoreV3).report &&
    (score as FullATSScoreV3).report.version === 3
  );
}
